import axios, { AxiosInstance } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second default timeout
});

let authToken: string | null = null;

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Send token to service worker for background sync
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_AUTH_TOKEN',
        token: token
      });
    }
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

export interface ApiErrorInfo {
  code: string | null;
  message: string;
  breakEndsLocal?: string;
}

/**
 * Normalize an axios error into {code, message, breakEndsLocal}.
 * Handles both payload shapes:
 *  - structured: detail = {error_code, message, details?: [{message: "HH:MM"}]}
 *  - legacy:     detail = "plain string"
 */
export const getErrorInfo = (error: any): ApiErrorInfo => {
  const detail = error?.response?.data?.detail;

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const code = typeof detail.error_code === 'string' ? detail.error_code : null;
    const message = typeof detail.message === 'string' ? detail.message : '';
    let breakEndsLocal: string | undefined;
    if (Array.isArray(detail.details) && detail.details.length > 0) {
      const first = detail.details[0];
      if (first && typeof first.message === 'string') {
        breakEndsLocal = first.message;
      }
    }
    return { code, message, breakEndsLocal };
  }

  if (typeof detail === 'string') {
    return { code: null, message: detail };
  }

  return { code: null, message: error?.message || 'Request failed' };
};

// Schedule-boundary error codes: AUTH_1006 = outside working hours (any endpoint),
// BIZ_5007 = break in force (sessions start/resume only).
const BOUNDARY_ERROR_CODES = ['AUTH_1006', 'BIZ_5007'];

const dispatchBoundaryBlock = (info: ApiErrorInfo) => {
  window.dispatchEvent(
    new CustomEvent('boundary-block', {
      detail: {
        code: info.code,
        message: info.message,
        breakEndsLocal: info.breakEndsLocal,
      },
    })
  );
};

export const setupAxiosInterceptors = () => {
  // Request interceptor
  api.interceptors.request.use(
    (config) => {
      // Add timestamp to prevent caching
      if (config.method === 'get') {
        config.params = {
          ...config.params,
          _t: Date.now(),
        };
      }
      
      // Ensure auth token is included in all requests
      const token = localStorage.getItem('access_token');
      if (token && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  let isRefreshing = false;
  let refreshSubscribers: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

  const subscribeToRefresh = (resolve: (token: string) => void, reject: (err: any) => void) => {
    refreshSubscribers.push({ resolve, reject });
  };

  const onRefreshed = (token: string) => {
    refreshSubscribers.forEach(({ resolve }) => resolve(token));
    refreshSubscribers = [];
  };

  const onRefreshFailed = (err: any) => {
    refreshSubscribers.forEach(({ reject }) => reject(err));
    refreshSubscribers = [];
  };

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Surface schedule-boundary blocks (outside hours / break in force) to the app
      // before any rejection so screens can react (BoundarySheet, Login message, ...).
      const errorInfo = getErrorInfo(error);
      if (errorInfo.code && BOUNDARY_ERROR_CODES.includes(errorInfo.code)) {
        dispatchBoundaryBlock(errorInfo);
      }

      const isAuthEndpoint = originalRequest.url?.includes('/auth/');
      
      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeToRefresh(
              (token: string) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                resolve(api(originalRequest));
              },
              reject
            );
            setTimeout(() => reject(new Error('Token refresh timeout')), 10000);
          });
        }

        isRefreshing = true;

        try {
          const response = await api.post('/auth/refresh');
          const { access_token } = response.data;
          
          setAuthToken(access_token);
          localStorage.setItem('access_token', access_token);
          
          isRefreshing = false;
          onRefreshed(access_token);
          
          originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshFailed(refreshError);

          // AUTH_1006 = outside working hours. Keep the stored token and the refresh
          // cookie so the app can sign back in automatically at the next in-hours
          // refresh. AuthContext clears the in-memory state; no hard redirect here.
          if (getErrorInfo(refreshError).code === 'AUTH_1006') {
            return Promise.reject(refreshError);
          }

          setAuthToken(null);
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }

      // If 401 on auth endpoint, clear tokens and redirect
      if (error.response?.status === 401 && isAuthEndpoint) {
        // Outside working hours: do not wipe credentials — see comment above.
        if (errorInfo.code === 'AUTH_1006') {
          return Promise.reject(error);
        }
        setAuthToken(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // If 403 (outside working hours)
      if (error.response?.status === 403) {
        console.warn('Access denied:', errorInfo.message || 'Access denied');
      }

      return Promise.reject(error);
    }
  );
};

// Session API calls
export const sessionAPI = {
  start: async (data: any) => {
    const response = await api.post('/sessions/start', data);
    return response.data;
  },

  complete: async (sessionId: string, data?: any) => {
    const response = await api.post(`/sessions/${sessionId}/complete`, data || {});
    return response.data;
  },

  getCurrent: async () => {
    const response = await api.get('/sessions/current');
    return response.data;
  },

  // Schedule-boundary state (break / duty end / overtime window + auto-stopped session)
  getBoundaryState: async () => {
    const response = await api.get('/sessions/boundary-state');
    return response.data;
  },

  // Resume a session that was auto-stopped at a schedule boundary
  resume: async (sessionId: string) => {
    const response = await api.post(`/sessions/${sessionId}/resume`, {});
    return response.data;
  },

  list: async (params?: any) => {
    const response = await api.get('/sessions', { params });
    return response.data;
  },

  uploadMedia: async (sessionId: string, file: File | Blob, capturedAt?: Date, requirementKey?: string) => {
    const formData = new FormData();
    
    // Ensure we have a File object with proper name
    if (file instanceof Blob && !(file instanceof File)) {
      file = new File([file], 'image.jpg', { type: file.type || 'image/jpeg' });
    }
    
    formData.append('file', file);
    if (capturedAt) {
      formData.append('captured_at', capturedAt.toISOString());
    }
    if (requirementKey) formData.append('requirement_key', requirementKey);   // IRB per-field photo (2026-11-02)

    // Calculate SHA256 hash from a copy
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const sha256 = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    formData.append('sha256', sha256);
    
    // Create new file from buffer since arrayBuffer consumes the file
    const newFile = new File([buffer], (file as File).name || 'image.jpg', { type: file.type });
    formData.set('file', newFile);

    // F4 FIX: the shared axios instance defaults to 'Content-Type: application/json', which makes
    // axios 1.12.2 JSON-stringify the FormData (files become {}), so uploads 422'd and no photo
    // ever reached the server. Force an identity transform + drop the JSON content-type so the
    // browser sets a proper multipart/form-data boundary.
    const response = await api.post(`/sessions/${sessionId}/media`, formData, {
      timeout: 60000,
      transformRequest: [(d) => d],
      headers: { 'Content-Type': undefined } as any,
    });
    return response.data;
  },

  // Material consumption APIs
  getMaterialsForActivity: async (activityCode: string) => {
    const response = await api.get(`/sessions/materials/by-activity/${activityCode}`);
    return response.data;
  },

  addMaterials: async (sessionId: string, materials: any[]) => {
    const response = await api.post(`/sessions/${sessionId}/materials`, materials);
    return response.data;
  },

  getSessionMaterials: async (sessionId: string) => {
    const response = await api.get(`/sessions/${sessionId}/materials`);
    return response.data;
  },

  deleteMaterial: async (sessionId: string, consumptionId: string) => {
    const response = await api.delete(`/sessions/${sessionId}/materials/${consumptionId}`);
    return response.data;
  },
};

// Jobs API calls
export const jobsAPI = {
  list: async (params?: any) => {
    const response = await api.get('/jobs', { params });
    return response.data;
  },

  get: async (jobId: string) => {
    const response = await api.get(`/jobs/${jobId}`);
    return response.data;
  },
};

// Activities API calls
export const activitiesAPI = {
  listTypes: async () => {
    const response = await api.get('/activities/types');
    return response.data;
  },
};

// Admin API calls
export const adminAPI = {
  getLiveBoard: async () => {
    const response = await api.get('/admin/live');
    return response.data;
  },

  getSummaryReport: async (params: any) => {
    const response = await api.get('/admin/reports/summary', { params });
    return response.data;
  },

  createJob: async (data: any) => {
    const response = await api.post('/admin/jobs', data);
    return response.data;
  },

  updateJob: async (jobId: string, data: any) => {
    const response = await api.patch(`/admin/jobs/${jobId}`, data);
    return response.data;
  },

  createActivityType: async (data: any) => {
    const response = await api.post('/admin/activity-types', data);
    return response.data;
  },

  updateActivityType: async (code: string, data: any) => {
    const response = await api.patch(`/admin/activity-types/${code}`, data);
    return response.data;
  },

  createTechnician: async (data: any) => {
    const response = await api.post('/admin/technicians', data);
    return response.data;
  },

  updateTechnician: async (technicianId: string, data: any) => {
    const response = await api.patch(`/admin/technicians/${technicianId}`, data);
    return response.data;
  },

  createScheduleRule: async (data: any) => {
    const response = await api.post('/admin/schedule/rules', data);
    return response.data;
  },

  createScheduleException: async (data: any) => {
    const response = await api.post('/admin/schedule/exceptions', data);
    return response.data;
  },

  createCorrection: async (sessionId: string, data: any) => {
    const response = await api.post(`/admin/sessions/${sessionId}/corrections`, data);
    return response.data;
  },

  exportExcel: async (params: any) => {
    const response = await api.get('/admin/export/excel', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  exportPDF: async (params: any) => {
    const response = await api.get('/admin/export/pdf', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  listTechnicians: async () => {
    const response = await api.get('/admin/technicians');
    return response.data;
  },

  // Overtime grants
  createOvertimeGrant: async (data: any) => {
    const response = await api.post('/admin/overtime', data);
    return response.data;
  },

  listOvertimeGrants: async (params?: any) => {
    const response = await api.get('/admin/overtime', { params });
    return response.data;
  },

  updateOvertimeGrant: async (groupId: string, data: any) => {
    const response = await api.patch(`/admin/overtime/${groupId}`, data);
    return response.data;
  },

  revokeOvertimeGrant: async (groupId: string, technicianId?: string) => {
    const response = await api.delete(`/admin/overtime/${groupId}`, {
      params: technicianId ? { technician_id: technicianId } : undefined,
    });
    return response.data;
  },

  // Overtime report
  getOvertimeReport: async (params: any) => {
    const response = await api.get('/admin/reports/overtime', { params });
    return response.data;
  },

  exportOvertimeReport: async (params: any) => {
    const response = await api.get('/admin/reports/overtime/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// PM (Projects / SOW) API calls — PM-4 technician queue + ex-stock fulfillment.
// All endpoints are additive: callers treat failures as "no PM data" so the
// non-PM technician flow renders unchanged.
export const pmAPI = {
  // Technician's PM activity queue (ready-first pre-sorted by the server)
  getMyQueue: async () => {
    const response = await api.get('/pm/my-queue');
    return response.data;
  },

  // PM-6 R9: SOW-scoped activity list for the picker (client fails OPEN)
  getAllowedActivities: async (jobNo: string) => {
    const response = await api.get(`/pm/jobs/${encodeURIComponent(jobNo)}/allowed-activities`);
    return response.data;
  },

  // PM-10a: Continue-your-work cards / hidden list / finish / hide / restore / link candidates
  getMyWork: async (includeHidden = false) => {
    const response = await api.get('/pm/my-work', { params: includeHidden ? { include_hidden: 1 } : {} });
    return response.data;
  },
  getHiddenWork: async () => {
    const response = await api.get('/pm/my-work/hidden');
    return response.data;
  },
  finishWorkItem: async (itemId: string, data: { scope: 'activity' | 'contribution'; idempotency_key: string; evidence?: any }) => {
    const response = await api.post(`/pm/work-items/${itemId}/finish`, data);
    return response.data;
  },
  hideWorkItem: async (itemId: string, data: { reason: string; details?: string; linked_checklist_item_id?: string | null; stop_first?: boolean }) => {
    const response = await api.post(`/pm/work-items/${itemId}/hide`, data);
    return response.data;
  },
  restoreWorkItem: async (itemId: string) => {
    const response = await api.post(`/pm/work-items/${itemId}/restore`, {});
    return response.data;
  },
  withdrawWorkItem: async (itemId: string) => {
    const response = await api.post(`/pm/work-items/${itemId}/withdraw`, {});
    return response.data;
  },
  getWorkItemLinks: async (itemId: string) => {
    const response = await api.get(`/pm/work-items/${itemId}/links`);
    return response.data;
  },

  // Ex-stock checklist items awaiting this session's technician
  getSessionExstock: async (sessionId: string) => {
    const response = await api.get(`/pm/sessions/${sessionId}/exstock`);
    return response.data;
  },

  // Technician confirms use of an ex-stock item (selection only — no rates)
  technicianFulfill: async (itemId: string, data: { session_id: string; qty: number; idempotency_key: string }) => {
    const response = await api.post(`/pm/checklist/${itemId}/technician-fulfill`, data);
    return response.data;
  },

  // Admin: eligible technicians + queue rows per activity of a SOW selection
  getEligible: async (selectionId: string) => {
    const response = await api.get(`/pm/sow/${selectionId}/eligible`);
    return response.data;
  },

  // Admin: assign an activity of a selection to a technician's queue
  assignQueue: async (data: { selection_id: string; activity_code: string; technician_id: string }) => {
    const response = await api.post('/pm/queue/assign', data);
    return response.data;
  },

  // Admin: skip an open queue row
  skipQueue: async (queueId: string) => {
    const response = await api.post(`/pm/queue/${queueId}/skip`, {});
    return response.data;
  },
};

// Translations API for multi-lingual support
export const translationsAPI = {
  // Get activity translations for a specific language
  getActivityTranslations: async (languageCode: string): Promise<Record<string, string>> => {
    try {
      const response = await api.get(`/translations/activities/lang/${languageCode}`);
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch activity translations:', error);
      return {}; // Return empty object on error, will fallback to English
    }
  },
  
  // Get UI translations for a specific language (if needed in future)
  getUITranslations: async (languageCode: string): Promise<Record<string, string>> => {
    try {
      const response = await api.get(`/translations/ui/${languageCode}`);
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch UI translations:', error);
      return {};
    }
  },
  
  // Save user's language preference
  setUserLanguage: async (languageCode: string): Promise<void> => {
    try {
      await api.post('/translations/user/language', { language: languageCode });
    } catch (error) {
      console.warn('Failed to save language preference:', error);
    }
  },
};
